import { Controller } from "@hotwired/stimulus";

// Hide/show table columns from an eye control in the header. The shared ui--popover
// handles open/close; this controller builds the column list, toggles cells by their
// data-orderable-column key, swaps the eye icons, and persists hidden keys per user+table.
export default class extends Controller {
  static targets = ["control", "list", "rowTemplate", "eyeOpen", "eyeOff", "eyeIcon", "eyeOffIcon"];
  static values = {
    table: String,
    userId: String,
    version: { type: Number, default: 1 },
  };

  connect() {
    // Nothing to toggle with 0 or 1 columns — hide the control entirely.
    if (this.columns().length < 2) {
      if (this.hasControlTarget) this.controlTarget.classList.add("hidden");
      return;
    }
    this.stored().forEach((key) => this.setColumnHidden(key, true));
    // The toggle guard keeps one column visible, but a stored set can still hide every
    // column here (e.g. views sharing a table key render different columns). Recover.
    if (this.visibleCount() === 0) {
      this.columns().forEach(({ key }) => this.setColumnHidden(key, false));
      this.persist([]);
    }
    this.buildList();
    this.updateIcon();
  }

  // The set of hideable columns, read from the (already-keyed) header cells.
  columns() {
    return [...this.element.querySelectorAll("thead th[data-orderable-column]")].map((th) => ({
      key: th.dataset.orderableColumn,
      label: (th.textContent || "").replace(/\s+/g, " ").trim() || th.dataset.orderableColumn,
    }));
  }

  // Clone the row template (markup lives in the partial) once per column.
  buildList() {
    if (!this.hasListTarget || !this.hasRowTemplateTarget) return;
    this.listTarget.replaceChildren();
    this.columns().forEach(({ key, label }) => {
      const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true);
      row.dataset.key = key;
      row.querySelector("[data-role='label']").textContent = label;
      row.querySelector("[data-role='icon']").appendChild(this.iconClone(!this.isHidden(key)));
      this.listTarget.appendChild(row);
    });
  }

  toggle(event) {
    const row = event.target.closest("[data-key]");
    const { key } = row.dataset;
    const hide = !this.isHidden(key);
    // Always leave at least one column visible.
    if (hide && this.visibleCount() <= 1) return;

    this.setColumnHidden(key, hide);
    row.querySelector("[data-role='icon']").replaceChildren(this.iconClone(!hide));

    const hidden = new Set(this.stored());
    hide ? hidden.add(key) : hidden.delete(key);
    this.persist([...hidden]);
    this.updateIcon();
  }

  iconClone(visible) {
    const template = visible ? this.eyeIconTarget : this.eyeOffIconTarget;
    return template.content.cloneNode(true);
  }

  isHidden(key) {
    const th = this.element.querySelector(`thead th[data-orderable-column="${CSS.escape(key)}"]`);
    return !!th && th.classList.contains("hidden");
  }

  setColumnHidden(key, hide) {
    this.element
      .querySelectorAll(`[data-orderable-column="${CSS.escape(key)}"]`)
      .forEach((cell) => cell.classList.toggle("hidden", hide));
  }

  visibleCount() {
    return this.columns().filter(({ key }) => !this.isHidden(key)).length;
  }

  // Eye when everything is shown; eye-off (crossed out) when any column is hidden.
  updateIcon() {
    if (!this.hasEyeOpenTarget || !this.hasEyeOffTarget) return;
    const anyHidden = this.columns().some(({ key }) => this.isHidden(key));
    this.eyeOpenTarget.classList.toggle("hidden", anyHidden);
    this.eyeOffTarget.classList.toggle("hidden", !anyHidden);
  }

  stored() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || [];
    } catch {
      return [];
    }
  }

  persist(hidden) {
    localStorage.setItem(this.storageKey, JSON.stringify(hidden));
  }

  // Meta-tag fallback for off-request renders (ActionCable broadcasts have no server-set user id).
  get resolvedUserId() {
    return this.userIdValue || document.querySelector('meta[name="current-user-id"]')?.content || "";
  }

  get storageKey() {
    return `columnVisibility.${this.tableValue}.v${this.versionValue}.user.${this.resolvedUserId}`;
  }
}
